import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Users } from 'lucide-react';
import FloatingDrawer from './FloatingDrawer';

/**
 * SpacesDrawer - Premium spaces/servers drawer with vertical list
 */
export default function SpacesDrawer({
  isOpen = false,
  onClose,
  spaces = [],
  onSpaceSelect,
  onAddSpace,
  loading = false
}) {
  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose} height="75vh">
      <div className="flex flex-col h-full bg-gjyl-dark">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <h2 className="text-xl font-bold text-white">Your Spaces</h2>
          <motion.button
            className="w-10 h-10 rounded-full bg-gjyl-accent flex items-center justify-center text-white"
            onClick={onAddSpace}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          >
            <Plus size={20} strokeWidth={2.5} />
          </motion.button>
        </div>

        {/* Scrollable spaces list */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {loading ? (
            <SkeletonLoader />
          ) : spaces.length === 0 ? (
            <EmptyState onAddSpace={onAddSpace} />
          ) : (
            <div className="space-y-1">
              {spaces.map((space, index) => (
                <SpaceItem
                  key={space.id}
                  space={space}
                  onClick={() => {
                    onSpaceSelect(space.id);
                    onClose();
                  }}
                  index={index}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </FloatingDrawer>
  );
}

/**
 * SpaceItem - Individual space list item with squircle icon
 */
function SpaceItem({ space, onClick, index }) {
  const hasUnreadMentions = space.unreadMentions > 0;
  const hasUnreadMessages = space.unreadMessages > 0 && !hasUnreadMentions;

  return (
    <motion.div
      className="relative flex items-center space-x-3 px-3 py-3 rounded-xl hover:bg-white/5 cursor-pointer select-none transition-colors"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
    >
      {/* Unread mention pip (left side) */}
      {hasUnreadMentions && (
        <motion.div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-8 bg-white rounded-r-full"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: index * 0.05 + 0.2, type: 'spring' }}
        />
      )}

      {/* Unread message pip (smaller) */}
      {hasUnreadMessages && (
        <motion.div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-gray-400 rounded-full"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: index * 0.05 + 0.2, type: 'spring' }}
        />
      )}

      {/* Squircle icon */}
      <div className="relative flex-shrink-0">
        <div className="w-14 h-14 rounded-2xl overflow-hidden">
          {space.icon_url ? (
            <img
              src={space.icon_url}
              alt={space.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
              <span className="text-2xl">{getSpaceEmoji(space.name)}</span>
            </div>
          )}
        </div>

        {/* Mention count badge (on icon) */}
        {space.unreadMentions > 0 && (
          <motion.div
            className="absolute -top-1 -right-1 bg-gjyl-danger rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 border-2 border-gjyl-dark"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: index * 0.05 + 0.3, type: 'spring' }}
          >
            <span className="text-xs font-bold text-white">
              {space.unreadMentions > 99 ? '99+' : space.unreadMentions}
            </span>
          </motion.div>
        )}
      </div>

      {/* Space info */}
      <div className="flex-1 min-w-0">
        <h3 className="text-white font-bold text-sm truncate mb-0.5">
          {space.name}
        </h3>
        <div className="flex items-center space-x-1.5 text-gray-400 text-xs">
          <Users size={12} />
          <span>{formatMemberCount(space.memberCount)} members</span>
        </div>
      </div>

      {/* Chevron indicator */}
      <div className="text-gray-600">
        <svg
          width="8"
          height="12"
          viewBox="0 0 8 12"
          fill="none"
          className="opacity-50"
        >
          <path
            d="M1.5 1L6.5 6L1.5 11"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </motion.div>
  );
}

/**
 * SkeletonLoader - Loading placeholder
 */
function SkeletonLoader() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center space-x-3 px-3">
          {/* Icon skeleton */}
          <motion.div
            className="w-14 h-14 rounded-2xl bg-zinc-800"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{
              repeat: Infinity,
              duration: 1.5,
              ease: 'easeInOut',
              delay: i * 0.1
            }}
          />

          {/* Text skeletons */}
          <div className="flex-1 space-y-2">
            <motion.div
              className="h-4 bg-zinc-800 rounded w-2/3"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{
                repeat: Infinity,
                duration: 1.5,
                ease: 'easeInOut',
                delay: i * 0.1
              }}
            />
            <motion.div
              className="h-3 bg-zinc-800 rounded w-1/2"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{
                repeat: Infinity,
                duration: 1.5,
                ease: 'easeInOut',
                delay: i * 0.1 + 0.2
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * EmptyState - No spaces placeholder
 */
function EmptyState({ onAddSpace }) {
  return (
    <motion.div
      className="flex flex-col items-center justify-center h-full text-center px-8 py-12"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <div className="w-20 h-20 rounded-2xl bg-zinc-800 flex items-center justify-center mb-4">
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="text-3xl"
        >
          🏠
        </motion.div>
      </div>
      <h3 className="text-white font-bold text-lg mb-2">No spaces yet</h3>
      <p className="text-gray-400 text-sm mb-6">
        Create or join a space to connect with communities
      </p>
      <motion.button
        className="px-6 py-2.5 bg-gjyl-accent text-white rounded-full font-semibold text-sm"
        onClick={onAddSpace}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Create Space
      </motion.button>
    </motion.div>
  );
}

/**
 * Get emoji for space based on name
 */
function getSpaceEmoji(name) {
  const lowercaseName = name.toLowerCase();

  if (lowercaseName.includes('game') || lowercaseName.includes('gaming')) return '🎮';
  if (lowercaseName.includes('music')) return '🎵';
  if (lowercaseName.includes('study') || lowercaseName.includes('learning')) return '📚';
  if (lowercaseName.includes('friend')) return '💬';
  if (lowercaseName.includes('work') || lowercaseName.includes('team')) return '💼';
  if (lowercaseName.includes('art') || lowercaseName.includes('creative')) return '🎨';
  if (lowercaseName.includes('tech') || lowercaseName.includes('dev')) return '💻';
  if (lowercaseName.includes('food') || lowercaseName.includes('cooking')) return '🍕';
  if (lowercaseName.includes('fitness') || lowercaseName.includes('gym')) return '💪';
  if (lowercaseName.includes('movie') || lowercaseName.includes('film')) return '🎬';

  // Default: use first letter
  return name.charAt(0).toUpperCase();
}

/**
 * Format member count for display
 */
function formatMemberCount(count) {
  if (!count) return '0';
  if (count < 1000) return count.toString();
  if (count < 10000) return `${(count / 1000).toFixed(1)}K`;
  if (count < 1000000) return `${Math.floor(count / 1000)}K`;
  return `${(count / 1000000).toFixed(1)}M`;
}
